import React, { useState, useEffect } from 'react';
import Modal from '../Modal';
import { Chauffeur, Camion } from '../../types';
import weighingService from '../../services/weighing';
import { TruckIcon } from '@heroicons/react/24/outline';

interface DriverVehiclesModalProps {
  isOpen: boolean;
  onClose: () => void;
  driver: Chauffeur;
  onVehicleDetached?: () => void;
}

const DriverVehiclesModal: React.FC<DriverVehiclesModalProps> = ({
  isOpen,
  onClose,
  driver,
  onVehicleDetached
}) => {
  const isDev = (import.meta.env as { MODE?: string }).MODE === 'development';
  const [vehicles, setVehicles] = useState<Camion[]>([]);
  const [detachingVehicle, setDetachingVehicle] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen && driver) {
      
      if (driver.camions_info && driver.camions_info.length > 0) {
        const vehiclesData = driver.camions_info.map(vehicleInfo => ({
          id: vehicleInfo.id,
          immatriculation: vehicleInfo.immatriculation,
          marque: vehicleInfo.marque,
          modele: vehicleInfo.modele || '',
          capacite: 0,
          proprietaire: '',
          nombre_passages: 0,
          tare_theorique: 0,
          created_at: '',
          updated_at: ''
        }));
        console.log('DriverVehiclesModal - Mapped vehicles:', vehiclesData);
        setVehicles(vehiclesData);
      } else {
        console.log('DriverVehiclesModal - No vehicles found, setting empty array');
        setVehicles([]);
      }
    }
  }, [isOpen, driver]);

  const handleDetachVehicle = async (vehicleId: number) => {
    if (!confirm('Êtes-vous sûr de vouloir détacher ce véhicule du chauffeur ?')) {
      return;
    }

    setDetachingVehicle(vehicleId);
    try {
     
      // Récupérer les véhicules actuels du chauffeur depuis camions_info
      const currentVehicleIds = driver.camions_info?.map(v => v.id) || [];
      const updatedVehicles = currentVehicleIds.filter(id => id !== vehicleId);
     
      // Mettre à jour le chauffeur avec la nouvelle liste de véhicules
      await weighingService.updateChauffeur(driver.id, {
        camions: updatedVehicles
      });
      
      // Mettre à jour la liste locale
      setVehicles(prev => {
        const filtered = prev.filter(vehicle => vehicle.id !== vehicleId);
        console.log('Updated local vehicles:', filtered);
        return filtered;
      });
      
      // Notifier le parent pour rafraîchir la liste
      if (onVehicleDetached) {
        onVehicleDetached();
      }
      
      alert('Véhicule détaché avec succès !');
    } catch (error) {
      console.error('Erreur lors du détachement du véhicule:', error);
      alert('Erreur lors du détachement du véhicule');
    } finally {
      setDetachingVehicle(null);
    }
  };


  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Véhicules de ${driver.prenom} ${driver.nom}`} size="lg">
      <div className="space-y-4">
        {/* Informations du chauffeur */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Informations du chauffeur</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Nom complet:</span>
              <span className="ml-2 font-medium">{driver.prenom} {driver.nom}</span>
            </div>
            <div>
              <span className="text-gray-500">Permis:</span>
              <span className="ml-2 font-medium">{driver.permis}</span>
            </div>
            <div>
              <span className="text-gray-500">Téléphone:</span>
              <span className="ml-2 font-medium">{driver.telephone || 'Non renseigné'}</span>
            </div>
            <div>
              <span className="text-gray-500">Email:</span>
              <span className="ml-2 font-medium">{driver.email || 'Non renseigné'}</span>
            </div>
          </div>
        </div>

        {/* Liste des véhicules */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">
            Véhicules associés ({vehicles.length})
          </h3>
          
          {/* Debug info */}
          {isDev && (
            <div className="mb-4 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
              <strong>Debug:</strong> camions_info: {driver.camions_info?.length || 0}, 
              camions: {driver.camions?.length || 0}, 
              vehicles state: {vehicles.length}
            </div>
          )}
          
          {vehicles.length === 0 ? (
            <div className="text-center py-8">
              <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun véhicule</h3>
              <p className="mt-1 text-sm text-gray-500">
                Ce chauffeur n'est associé à aucun véhicule.
              </p>
              {isDev && (
                <div className="mt-4 p-2 bg-gray-100 rounded text-xs">
                  <p>Données du chauffeur:</p>
                  <pre>{JSON.stringify(driver, null, 2)}</pre>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {vehicles.map((vehicle) => (
                <div
                  key={vehicle.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-3">
                    <TruckIcon className="h-8 w-8 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {vehicle.immatriculation}
                      </div>
                      <div className="text-sm text-gray-500">
                        {vehicle.marque} {vehicle.modele}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleDetachVehicle(vehicle.id)}
                      disabled={detachingVehicle === vehicle.id}
                      className="px-3 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-full hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {detachingVehicle === vehicle.id ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600"></div>
                      ) : (
                        'Détacher'
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DriverVehiclesModal;
